import type { ChatMessage, ChatThread, StreamEvent } from './types';

/**
 * Folding the assistant's event stream onto the thread.
 *
 * The reply arrives as a run of events against one placeholder message, so
 * every case here is "the same message, further on". Kept pure and away from
 * the hook: the awkward parts — the echoed question, the repeated tool call,
 * a `done` that restates the whole answer — are the parts worth testing, and
 * none of them need a socket to reach.
 */

function replaceMessage(
  thread: ChatThread,
  id: string,
  change: (message: ChatMessage) => ChatMessage,
): ChatThread {
  return {
    ...thread,
    messages: thread.messages.map((message) => (message.id === id ? change(message) : message)),
  };
}

/**
 * The assistant sometimes opens a reply by repeating the question back. It is
 * already on screen directly above, so a frame that is only the question is
 * dropped rather than shown twice.
 */
function isEcho(text: string, userMessage: string): boolean {
  return text.trim() === userMessage.trim();
}

export function applyStreamEvent(
  thread: ChatThread,
  assistantMessageId: string,
  event: StreamEvent,
  userMessage: string,
): ChatThread {
  switch (event.type) {
    case 'metadata':
      // The service assigns the real thread id; ours was a placeholder so the
      // first request had something to send.
      return event.thread_id && event.thread_id !== thread.id
        ? { ...thread, id: event.thread_id }
        : thread;

    case 'content':
      if (event.delta) {
        if (isEcho(event.delta, userMessage)) return thread;
        const delta = event.delta;
        return replaceMessage(thread, assistantMessageId, (message) => ({
          ...message,
          content: message.content + delta,
        }));
      }
      if (event.content !== undefined) {
        // A whole-content frame replaces what is there. An empty one is
        // ignored unless nothing has arrived yet, so a trailing blank frame
        // cannot wipe a finished answer.
        const content = event.content;
        if (content === '' && thread.messages.some((m) => m.id === assistantMessageId && m.content))
          return thread;
        if (isEcho(content, userMessage)) return thread;
        return replaceMessage(thread, assistantMessageId, (message) => ({ ...message, content }));
      }
      return thread;

    case 'tool_call': {
      if (!event.tool || !event.args) return thread;
      const { tool, args } = event;
      return replaceMessage(thread, assistantMessageId, (message) => {
        const toolCalls = message.toolCalls ?? [];
        // The same call can be announced more than once as it runs; one badge
        // per distinct call.
        const known = toolCalls.some(
          (call) => call.tool === tool && JSON.stringify(call.args) === JSON.stringify(args),
        );
        return known ? message : { ...message, toolCalls: [...toolCalls, { tool, args }] };
      });
    }

    case 'done':
      return replaceMessage(thread, assistantMessageId, (message) => ({
        ...message,
        content:
          typeof event.content === 'string' && event.content.trim() !== ''
            ? event.content
            : message.content,
        isLoading: false,
      }));

    case 'error':
      return replaceMessage(thread, assistantMessageId, (message) => ({
        ...message,
        content: `Error: ${event.error}`,
        isLoading: false,
      }));

    default:
      return thread;
  }
}

/** Clears the loading flag when the stream ends, however it ended. */
export function finishMessage(thread: ChatThread, id: string, content?: string): ChatThread {
  return replaceMessage(thread, id, (message) => ({
    ...message,
    content: content ?? message.content,
    isLoading: false,
  }));
}
